import { Box2, Camera, Color, Raycaster, Vector2, Vector3 } from "three";
import { SCENE_TYPE, RaycasterSettings, ITextScale } from "../config/ViewerConfig";
import { Viewer } from "../viewer";
import { HeightMapHandler, IVolumeInfo } from "./HeightMapHandler";
import { PointCloudLoaderFactory } from "./loader/factory";
import {
  IPointCloudLoadParams,
  IPointCloudTileGeometry,
} from "./loader/interface";
import { IPointcloudSectionSelection, PointCloudFactory } from "./PointCloud";
import { PointCloudBVH } from "./PointCloudBVH";
import { PointCloudStreamHandler } from "./PointCloudStreamHandler";
import { IPointColorRangeInfo, PointCloudTile } from "./PointCloudTile";
import { PointsMaterialHandler } from "./PointCloudMaterialHandler";

//for streaming
export class PointCloudManager {
  // private _offsetMatrix: Matrix4;
  private _viewer: Viewer;
  private _heightMapHandler: HeightMapHandler;
  private _pointcloudStreamHandler: PointCloudStreamHandler;
  private _pointcloudFactory: PointCloudFactory;
  private _pointcloudLoaderFactory: PointCloudLoaderFactory;
  private _pointsMaterialHandler: PointsMaterialHandler;

  public bvh: PointCloudBVH;

  public activeSelectionInfo: {
    shapes: IPointcloudSectionSelection;
    color: Color;
  };

  // public get offsetMatrix() {
  //   return this._offsetMatrix;
  // }

  public get pointMaterial() {
    return this._pointsMaterialHandler.pointMaterial;
  }

  constructor(viewer: Viewer, raycasterSettings: RaycasterSettings) {
    this._viewer = viewer;
    // this._offsetMatrix = new Matrix4().identity();
    this.bvh = new PointCloudBVH(viewer);
    this._pointcloudFactory = new PointCloudFactory(raycasterSettings);
    this._pointcloudLoaderFactory = new PointCloudLoaderFactory();
    this._pointsMaterialHandler = new PointsMaterialHandler(this._viewer);
  }

  public clear() {
    // this._offsetMatrix.identity();
    this.bvh.clear();
    this._viewer.pointMaterial.dispose();
  }

  public async onCameraChange(
    camera: Camera,
    abortController: AbortController
  ) {
    if (this._pointcloudStreamHandler) {
      return this._pointcloudStreamHandler.onCameraChange(
        camera,
        abortController
      );
    }
  }

  public intersectPointCloud(
    gl: WebGLRenderingContext,
    pickCoord: { x: number; y: number },
    refine = false
  ): Vector3 | undefined {
    const tileMap = this._pointcloudStreamHandler.getTileMapPickIndexToUrl();
    return this.bvh.intersectPointCloud(tileMap, gl, pickCoord, refine);
  }

  public intersectPointCloudFromRay(e: Raycaster): Vector3 | undefined {
    return this.bvh.intersectPointCloudFromRay(e);
  }

  public async loadPointCloud(
    type: string,
    projectId: string,
    timestamp: string,
    params: IPointCloudLoadParams,
    abortController: AbortController,
    loadId: number
  ) {
    // create or load tile -> bvh
    const loader = this._pointcloudLoaderFactory.getLoader(params.type);
    if (loader) {
      const response = await loader.load(
        params,
        abortController,
        type,
        projectId,
        timestamp
      );

      if ((response as any).root) {
        // 分割

        if ((response as any).root instanceof PointCloudTile) {
          this.bvh.setRootTile((response as any).root);
          this.bvh.offset = new Vector3().fromArray((response as any).offset);
          this._pointcloudStreamHandler = new PointCloudStreamHandler(
            this._viewer,
            this._pointsMaterialHandler,
            this.bvh.rootTile,
            params.url as string,
            this._pointcloudFactory,
            type,
            projectId,
            timestamp
          );
          const rgbGroup = this._pointcloudStreamHandler.getRgbGroup();

          if (loadId === this._viewer.loadId) {
            this._viewer.getScene(SCENE_TYPE.STREAM_POINTCLOUD).add(rgbGroup);
          } else {
            console.warn("Loading duplicate point cloud");
          }

          const debuggroup = this._pointcloudStreamHandler.getDebugRenderable();
          this._viewer.getScene(SCENE_TYPE.STREAM_POINTCLOUD).add(debuggroup);

          // load root tile
          this._pointcloudStreamHandler
            .fetchAndRenderTiles(
              [this.bvh.rootTile],
              ["first"],
              [type],
              [projectId],
              abortController,
              timestamp
            )
            .then((tiles) => {
              this._viewer.Resize();
              this._viewer.fit();
            });

          this._heightMapHandler = new HeightMapHandler();
          await this._heightMapHandler.loadHeightMap(
            this.bvh.rootTile.boundingBox.clone(),
            this.bvh.offset,
            params.gridSize,
            this.bvh.offset.z,
            abortController,
            type,
            projectId,
            timestamp
          );
        }
      } else {
        // 非分割

        this._pointcloudStreamHandler = new PointCloudStreamHandler(
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined
        );

        this._pointcloudStreamHandler.setTileMapPickIndexToUrlForOriginal();

        this.bvh.setRootTileForOriginal(new PointCloudTile());
        this.bvh.rootTile.boundingBox = (
          response as IPointCloudTileGeometry
        ).bbox;
        this.bvh.offset.copy(response.offset as Vector3);

        const geo = response as IPointCloudTileGeometry;
        const indices = this._pointcloudStreamHandler.makePickIndex(
          geo.vertices.length / 3
        );

        this.bvh.rootTile.pointcloud = this._pointcloudFactory.createPointCloud(
          {
            vertices: geo.vertices,
            color: geo.color,
            indices: indices,
          },
          this._pointsMaterialHandler.pointMaterial,
          this.bvh.rootTile,
          { orthographic: 0.09, perspective: 0.18 } as ITextScale,
          this._pointsMaterialHandler.createPickMaterial(0)
        );

        if (loadId === this._viewer.loadId) {
          this._viewer
            .getScene(SCENE_TYPE.STATIC_POINTCLOUD)
            .add(this.bvh.rootTile.pointcloud.getMesh());
        } else {
          console.warn("Loading duplicate point cloud");
        }
      }
    }
  }

  public calculateVolumeInfo(
    polygon: Vector3[],
    meshIndices: number[]
  ): IVolumeInfo {
    const result = this._heightMapHandler.getVolumeInfoUnderPolygon(
      {
        vertices: polygon,
        indices: meshIndices,
      },
      this.bvh.offset
    );
    return result;
  }

  public applyHeightBasedColor(
    polygon: { vertices: Vector3[]; indices: number[] },
    rangeInfo: IPointColorRangeInfo
  ) {
    this.bvh.rootTile.applyHeightBasedColor(
      rangeInfo,
      polygon,
      new Box2().setFromPoints(polygon.vertices as unknown as Vector2[])
    );
  }

  public setHeatMapVisibility(visibility: boolean) {
    this._pointcloudStreamHandler.setHeatMapVisibility(visibility);
  }

  public setHeightMapVisibility(visibility: boolean) {
    this._heightMapHandler.setHeightMapVisibility(visibility, this._viewer);
  }

  public getSize(): Vector3 | undefined {
    if (this.bvh.rootTile) {
      const size = new Vector3();
      this.bvh.rootTile.boundingBox.getSize(size);
      return size;
    }
  }

  public getCenter(): Vector3 | undefined {
    if (this.bvh.rootTile) {
      const center = new Vector3();
      this.bvh.rootTile.boundingBox.getCenter(center);
      return center;
    }
  }

  public setPointCloudVisibility(visibility: boolean) {
    this.bvh.setVisibility(visibility);
  }

  public setPointSize(size: number) {
    this.bvh.setPointSize(size);
  }

  public highlightSelectedPointCloudSection(
    selectionPlanes: IPointcloudSectionSelection,
    color: Color
  ) {
    // this.activeSelectionInfo = {shapes: selectionPlanes, color};
    // this.bvh.highlightSelectedPointCloudSection(this.activeSelectionInfo.shapes, this.activeSelectionInfo.color);
    this._pointsMaterialHandler.highlightSelectedPointCloudSection(
      selectionPlanes,
      color
    );
  }

  public resetHighlightSelectedPointCloudSection() {
    // this.bvh.resetHighlightSelectedPointCloudSection();
    this._pointsMaterialHandler.resetHighlightSelectedPointCloudSection();
  }

  public resetPointCloudColor() {
    this.bvh.resetPointCloudColor();
  }

  public removeSelectedPoints(selectionColor: Color) {
    this.bvh.removeSelectedPoints(selectionColor);
  }

  public setTileSplit(thresHold: number) {
    this._pointcloudStreamHandler.setTileSplit(thresHold);
  }

  public getTileSplit() {
    return this._pointcloudStreamHandler.getTileSplit();
  }

  public toggleIsDebug() {
    this._pointcloudStreamHandler.toggleIsDebug();
  }
}
